import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { AccordionButton } from "../Buttons/AccordionButton";
import { CloseButton } from "../Buttons/CloseButton";
import * as DocumentReader from "../DocumentReader";
import * as Share from "../Shared";
import { IContentGroupAccordionStatus } from "../States/AreaUiInfo";
import { DocumentSearchStore } from "../Stores/DocumentSearchStore";
import { Strings } from "../Stores/StringsStore";

interface IDocumentFilterValueProps extends IDocumentFilterFieldSetPropsBase {
	value: DocumentReader.SearchFilterValue;
	parent: DocumentReader.SearchFilterValue;
}

interface IDocumentFilterFieldSetProps extends IDocumentFilterFieldSetPropsBase {
	displayValues: DocumentReader.SearchFilterValue[];
}

interface IDocumentFilterFieldSetPropsBase extends IDocumentFilterBaseProps {
	onSelect: () => void;
}

interface IDocumentFilterProps extends IDocumentFilterBaseProps {
	onSelect: (filter: string, position: number) => void;
}

interface IDocumentFilterBaseProps {
	filter: DocumentReader.DocumentSearchFilter;
	store?: DocumentSearchStore;
	uiContext?: IContentGroupAccordionStatus;
	collapsed?: boolean;
	testId?: string;
	testName?: string;
}

@inject('store')
@observer
export class DocumentFilter extends React.Component<IDocumentFilterProps, {}> {

	store: DocumentSearchStore = null;
	@observable collapsed = false;

	componentWillMount() {
		this.store = this.props.store;
	}

	componentDidMount() {
		if (this.props.uiContext && this.props.uiContext.contentGroupAccordionStatus.has(this.props.filter.name)) {
			this.collapsed = this.props.uiContext.contentGroupAccordionStatus.get(this.props.filter.name);
		}
		else {
			this.collapsed = this.props.collapsed;
		}
	}

	onSelect = () => {
		let element = document.getElementById(this.props.filter.caption);
		let currentPosition = element.getBoundingClientRect().top;
		this.props.onSelect(this.props.filter.caption, currentPosition);
	}

	handleClick = (e: React.MouseEvent<any>) => {
		this.collapsed = !this.collapsed;
		if (this.props.uiContext) {
			this.props.uiContext.contentGroupAccordionStatus.set(this.props.filter.name, this.collapsed);
		}
		e.stopPropagation();
	}

	@action handleAddAditionalFilterValues = () => {
		this.store.additionalFilterValuesWizardState.wizardCaption = this.props.filter.caption;
		this.store.additionalFilterValuesWizardState.currentFilterToAddFilterValues = this.props.filter;
		this.store.additionalFilterValuesWizardState.open();
	}

	@computed get buttonTooltip() {
		return this.collapsed ? Strings.general.expandButtonTooltip : Strings.general.collapseButtonTooltip;
	}

	@computed get className() {
		let className = this.collapsed ? "filtergroup" : "filtergroup is--group-open";
		if (this.props.filter.filterMode === Share.FilterMode.MultipleAnd) {
			className += " multipleand";
		}
		else if (this.props.filter.filterMode === Share.FilterMode.MultipleOr) {
			className += " multipleor";
		}
		return className;
	}

	@computed get displayValues() {
		let hasChanged = this.props.store.isBusy && this.props.filter.values.some(v => v === this.props.store.lastDeselectedFilterValue);
		let values;
		if (this.props.filter.isHierarchy) {
			let selectedFilters = this.props.filter.values.filter(v => isSelected(v));
			values = selectedFilters.length === 0 ? this.props.filter.values : selectedFilters;
		}
		else {		
			if (hasChanged) {
				values = this.props.filter.values;
			}
			else {
				values = this.props.filter.values.filter(v => v.occurrences > 0);
            }	
		}		
		return values;
	}

	render() {	
		return <div id={this.props.filter.caption} className={this.className} data-test-id={this.props.filter.caption} data-test-name={this.props.testName}>
			<h2>
				<span className="filtergroup__label" onClick={this.handleClick}>{this.props.filter.caption}</span>
				<span className="filtergroup__control">
					<div className="tinytool">
						<AccordionButton onClick={this.handleClick} title={this.buttonTooltip} />
					</div>
				</span>
			</h2>
			<div className="filtergroup-content">
				<DocumentFilterFieldSet filter={this.props.filter} onSelect={this.onSelect} displayValues={this.displayValues} />
				{this.displayValues.length > this.props.filter.maxValues ? <a data-test-name="more" className="filtergroup-content__more" onClick={this.handleAddAditionalFilterValues}>{Strings.general.more}...</a> : null}
			</div>
		</div>;
		
	}
}

export function isSelected(value: DocumentReader.SearchFilterValue) {
	return value.isSelected || childrenSelected(value);
}

function childrenSelected(value: DocumentReader.SearchFilterValue): boolean {
	return value.children.some(v => v.isSelected || childrenSelected(v));
}

@inject('store')
@observer
class DocumentFilterFieldSet extends React.Component<IDocumentFilterFieldSetProps, {}> {
	
	render() {	
		return <fieldset>			
			{				
				this.props.displayValues.map((value, index) => {
					return index < this.props.filter.maxValues || value.isSelected  ? <DocumentFilterValue key={this.props.filter.name + value.caption} filter={this.props.filter} value={value} onSelect={this.props.onSelect} parent={null} /> : null;
			})}
		</fieldset>;
	}
}

@inject('store')
@observer
class DocumentFilterValue extends React.Component<IDocumentFilterValueProps, {}> {

	@action onSelectFilter = () => {
		this.props.onSelect();
		this.props.value.isSelected = !this.props.value.isSelected;
		if (!this.props.value.isSelected) {
			this.props.store.lastDeselectedFilterValue = this.props.value;
		}
		this.props.store.setDocumentFilter(this.props.filter, this.props.value);
		this.props.store.triggerSearch(false);
	}

	handleChange = () => { }

	@computed get className() {
		let className = "custom-radiocheck";
		if (this.props.filter.filterMode === Share.FilterMode.MultipleAnd || this.props.filter.filterMode === Share.FilterMode.MultipleOr) {
			if (this.props.value.isSelected) {
				className += " checked";
			}
			let index = this.props.filter.values.indexOf(this.props.value);
			let hasNextCheckedValue = this.props.filter.values.some((v, i) => i > index && v.isSelected);
			if (!hasNextCheckedValue) {
				className += " last";
			}
		}
		if (this.props.filter.isHierarchy) {
			className += " hierarchy";
			if (!isSelected(this.props.value)) {
				className += "-choose";
			}
		}
		return className;
	}

	@action onSelect = () => {
		this.props.onSelect();
		this.props.value.isSelected = !this.props.value.isSelected;
		if (!this.props.value.isSelected) {
			this.props.store.lastDeselectedFilterValue = this.props.value;
		}
		this.props.store.setDocumentFilter(this.props.filter, this.props.value);
		if (!this.props.value.isSelected && this.props.parent) {
			this.props.parent.isSelected = true;
			this.props.store.setDocumentFilter(this.props.filter, this.props.parent);
		}
		this.props.store.triggerSearch(true);
	}

	@action onClose = () => {
		this.props.onSelect();
		this.props.value.isSelected = false;
		this.props.store.lastDeselectedFilterValue = this.props.value;
		if (this.props.parent) {
			this.props.parent.isSelected = true;
			this.props.store.setDocumentFilter(this.props.filter, this.props.parent);
		}
		else {
			this.props.store.setDocumentFilter(this.props.filter, this.props.value);
		}
		this.props.store.resetFilterValuesOfTypeSingle(this.props.value, this.props.filter.values);
		this.props.store.triggerSearch(true);
	}

	render(): JSX.Element {		
		let selectedChildren = this.props.value.children && (this.props.value.isSelected ? this.props.value.children : this.props.value.children.filter(c => isSelected(c)));	
		let isValueSelected = this.props.filter.isHierarchy && (this.props.value.isSelected || selectedChildren.length > 0);
		
		return <>
			<label className={this.className} data-test-name="filterValueRadioCheck" data-test-id={this.props.value.caption}>
				<input type={this.props.filter.filterMode === Share.FilterMode.Single ? "radio" : "checkbox"} name={this.props.filter.name} checked={this.props.value.isSelected} onChange={this.handleChange} value={this.props.value.value} />
				{
					!this.props.filter.isHierarchy
						? <span className={this.props.filter.filterMode === Share.FilterMode.Single ? "radio-style" : "checkbox-style"} onClick={this.onSelect}></span>
						: null
				}
				<span className="filtervalue-label" onClick={this.onSelect}>
					{this.props.value.caption}
				</span>
				{
					isValueSelected
						? <div className="tinytool"><CloseButton onClick={this.onClose} /></div>
						: <span className={this.props.value.occurrences > 0 ? "filtergroup-content__count" : "filtergroup-content__count is--occurrences-none"}>{this.props.value.occurrences}</span>
				}
			</label>
			{selectedChildren.map(c => <DocumentFilterValue key={this.props.filter.name + c.caption} filter={this.props.filter} value={c} onSelect={this.props.onSelect} parent={this.props.value} />)}
		</>;
	}
}
